import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Send, PackageCheck, Trash2, Eye, Hash, Search, ChevronDown, ChevronUp, XCircle, AlertTriangle, Zap, RefreshCw, Pencil, Printer, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  draft: 'bg-zinc-700 text-zinc-300',
  sent: 'bg-blue-500/20 text-blue-400',
  partial: 'bg-yellow-500/20 text-yellow-400',
  received: 'bg-green-500/20 text-green-400',
  closed: 'bg-orange-500/20 text-orange-400',
  cancelled: 'bg-red-500/20 text-red-400',
};

export default function AdminPurchaseOrders() {
  const [pos, setPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [form, setForm] = useState({
    supplier_id: '',
    items: [{ product_id: '', quantity: 1, cost_price: 0 }],
    notes: '',
    expected_date: '',
  });
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiveNotes, setReceiveNotes] = useState('');
  const [serialNumbers, setSerialNumbers] = useState({});
  const [lastReceivedSerials, setLastReceivedSerials] = useState([]);
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [viewSerials, setViewSerials] = useState([]);
  const [expandedItem, setExpandedItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletePO, setDeletePO] = useState(null);
  const [deleteSummary, setDeleteSummary] = useState(null);
  const [reverseStock, setReverseStock] = useState(true);
  const [editingPO, setEditingPO] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [posRes, suppliersRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/purchase-orders`, authHeaders),
        axios.get(`${API}/admin/suppliers`, authHeaders),
        axios.get(`${API}/products?limit=50`),
      ]);
      setPOs(posRes.data);
      setSuppliers(suppliersRes.data.filter(s => s.is_active !== false));
      setProducts(productsRes.data.products || []);
    } catch (err) { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    try {
      const validItems = form.items.filter(i => i.product_id && i.quantity > 0);
      if (validItems.length === 0) { toast.error('Add at least one item'); return; }
      await axios.post(`${API}/admin/purchase-orders`, { ...form, items: validItems }, authHeaders);
      toast.success('Purchase order created');
      setShowCreateModal(false); resetForm(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create PO'); }
  };

  const handleSendPO = async (poId) => {
    try {
      const res = await axios.post(`${API}/admin/purchase-orders/${poId}/send`, {}, authHeaders);
      toast.success(res.data.message); fetchData();
    } catch (err) { toast.error('Failed to send PO'); }
  };

  const handleConfirmManual = async (poId) => {
    try {
      const res = await axios.post(`${API}/admin/purchase-orders/${poId}/confirm-manual`, {}, authHeaders);
      toast.success(res.data.message); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to confirm PO'); }
  };

  const handleResendPO = async (poId) => {
    try {
      const res = await axios.post(`${API}/admin/purchase-orders/${poId}/resend`, {}, authHeaders);
      toast.success(res.data.message || 'PO resent successfully'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend PO'); }
  };

  const openReceiveModal = (po) => {
    setSelectedPO(po);
    const items = po.items.map(item => {
      const prod = products.find(p => p.id === item.product_id);
      return {
        product_id: item.product_id,
        product_name: item.product_name,
        sku: item.sku,
        quantity_ordered: item.quantity,
        quantity_received: item.quantity_received || 0,
        quantity_to_receive: 0,
        track_serials: prod?.track_serials || false,
      };
    });
    setReceiveItems(items);
    setReceiveNotes('');
    const sns = {};
    items.forEach(i => { sns[i.product_id] = []; });
    setSerialNumbers(sns);
    setShowReceiveModal(true);
  };

  const handleReceivePO = async () => {
    try {
      const items = receiveItems
        .filter(i => i.quantity_to_receive > 0)
        .map(i => ({
          product_id: i.product_id,
          quantity_received: i.quantity_to_receive,
          serial_numbers: (serialNumbers[i.product_id] || []).filter(s => s.trim()),
        }));
      if (items.length === 0) { toast.error('Enter quantities to receive'); return; }
      const res = await axios.post(`${API}/admin/purchase-orders/${selectedPO.id}/receive`, { items, notes: receiveNotes }, authHeaders);
      toast.success(`Stock received - Status: ${res.data.status}`);
      setShowReceiveModal(false);
      // If serials were generated, offer to print labels
      if (res.data.generated_serials?.length > 0) {
        setLastReceivedSerials(res.data.generated_serials);
        setShowLabelDialog(true);
      }
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to receive stock'); }
  };

  const handlePrintLabels = async (serials) => {
    try {
      const res = await axios.post(`${API}/admin/serial-labels`, { serials }, { ...authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success(`Label PDF generated for ${serials.length} serial(s)`);
    } catch (err) { toast.error('Failed to generate labels'); }
  };

  const handlePrintLabelsForPO = async (poId) => {
    try {
      const res = await axios.post(`${API}/admin/serial-labels`, { po_id: poId }, { ...authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success('Label PDF generated');
    } catch (err) { toast.error('Failed to generate labels'); }
  };

  const handleCancelPO = async (poId) => {
    if (!window.confirm('Cancel this PO?')) return;
    try {
      await axios.delete(`${API}/admin/purchase-orders/${poId}`, authHeaders);
      toast.success('PO cancelled'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel PO'); }
  };

  const handleClosePO = async (poId) => {
    if (!window.confirm('Close this PO? This accepts what has been received and stops expecting remaining items.')) return;
    try {
      const res = await axios.post(`${API}/admin/purchase-orders/${poId}/close`, {}, authHeaders);
      toast.success(`PO closed. Received ${res.data.received}/${res.data.ordered} items.`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to close PO'); }
  };

  const openDeleteDialog = async (po) => {
    setDeletePO(po);
    setReverseStock(true);
    try {
      const res = await axios.get(`${API}/admin/purchase-orders/${po.id}/summary`, authHeaders);
      setDeleteSummary(res.data);
    } catch { setDeleteSummary(null); }
    setDeleteDialogOpen(true);
  };

  const handleForceDelete = async () => {
    if (!deletePO) return;
    try {
      const res = await axios.delete(`${API}/admin/purchase-orders/${deletePO.id}/force?reverse_stock=${reverseStock}`, authHeaders);
      toast.success(`PO deleted${res.data.stock_reversed ? ` — ${res.data.items_reversed} items reversed from stock` : ''}`);
      setDeleteDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete PO'); }
  };

  const handleAutoFillLowStock = async () => {
    if (!form.supplier_id) { toast.error('Select a supplier first'); return; }
    try {
      const res = await axios.get(`${API}/admin/suppliers/${form.supplier_id}/low-stock-products`, authHeaders);
      const lowStock = res.data.products || [];
      if (lowStock.length === 0) { toast.info('No low-stock products found for this supplier'); return; }
      const newItems = lowStock.map(p => ({
        product_id: p.product_id,
        quantity: p.suggested_qty,
        cost_price: Math.round(p.cost_price * 100) / 100,
      }));
      setForm({ ...form, items: newItems });
      toast.success(`Added ${newItems.length} low-stock items`);
    } catch (err) { toast.error('Failed to fetch low-stock products'); }
  };

  const loadSerials = async (poId) => {
    try {
      const res = await axios.get(`${API}/admin/serial-numbers?po_id=${poId}`, authHeaders);
      setViewSerials(res.data.serial_numbers || []);
    } catch (err) { setViewSerials([]); }
  };

  const openViewModal = (po) => {
    setSelectedPO(po);
    setViewSerials([]);
    setExpandedItem(null);
    setShowViewModal(true);
    if (po.status === 'partial' || po.status === 'received') {
      loadSerials(po.id);
    }
  };

  const updateSerialInput = (productId, value) => {
    // Split by newlines to support pasting multiple serials
    const lines = value.split('\n');
    setSerialNumbers({ ...serialNumbers, [productId]: lines });
  };

  const resetForm = () => {
    setForm({ supplier_id: '', items: [{ product_id: '', quantity: 1, cost_price: 0 }], notes: '', expected_date: '' });
    setEditingPO(null);
  };

  const openEditModal = (po) => {
    setEditingPO(po);
    setForm({
      supplier_id: po.supplier_id || '',
      items: (po.items || []).map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        cost_price: item.cost_price || 0,
      })),
      notes: po.notes || '',
      expected_date: po.expected_date || '',
    });
    setShowCreateModal(true);
  };

  const handleUpdatePO = async (e) => {
    e.preventDefault();
    if (!editingPO) return;
    try {
      const validItems = form.items.filter(i => i.product_id && i.quantity > 0);
      if (validItems.length === 0) { toast.error('Add at least one item'); return; }

      // Enrich items with product details
      const enrichedItems = validItems.map(item => {
        const prod = products.find(p => p.id === item.product_id);
        return {
          product_id: item.product_id,
          product_name: prod?.name || '',
          sku: prod?.sku || '',
          quantity: item.quantity,
          cost_price: item.cost_price,
          quantity_received: 0,
        };
      });

      const subtotal = enrichedItems.reduce((sum, i) => sum + (i.quantity * i.cost_price), 0);
      const tax = subtotal * 0.1;

      await axios.put(`${API}/admin/purchase-orders/${editingPO.id}`, {
        supplier_id: form.supplier_id,
        supplier_name: suppliers.find(s => s.id === form.supplier_id)?.name || editingPO.supplier_name,
        items: enrichedItems,
        notes: form.notes,
        expected_date: form.expected_date,
        subtotal,
        tax,
        total: subtotal + tax,
        updated_at: new Date().toISOString(),
      }, authHeaders);

      toast.success('Purchase order updated');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update PO'); }
  };
  const addLineItem = () => { setForm({ ...form, items: [...form.items, { product_id: '', quantity: 1, cost_price: 0 }] }); };
  const updateLineItem = (index, field, value) => {
    const n = [...form.items];
    n[index] = { ...n[index], [field]: value };
    // When product changes, set qty to reorder_qty or supplier_moq and prefill cost
    if (field === 'product_id') {
      const prod = products.find(p => p.id === value);
      if (prod) {
        n[index].quantity = prod.reorder_qty || prod.supplier_moq || 1;
        if (!n[index].cost_price) n[index].cost_price = prod.cost_price || 0;
      }
    }
    setForm({ ...form, items: n });
  };
  const removeLineItem = (index) => { if (form.items.length === 1) return; setForm({ ...form, items: form.items.filter((_, i) => i !== index) }); };
  const getTotal = () => form.items.reduce((sum, item) => sum + (item.quantity * item.cost_price), 0);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const fmtTime = (d) => d ? new Date(d).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-purchase-orders">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-black text-white uppercase tracking-tight">Purchase Orders</h1>
            <p className="text-zinc-400 mt-1">Create and manage supplier orders</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="create-po-btn">
            <Plus className="h-4 w-4 mr-2" /> New Purchase Order
          </Button>
        </div>

        {/* PO List */}
        <div className="space-y-3">
          {loading ? <div className="text-zinc-400">Loading...</div> : pos.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-8 text-center"><p className="text-zinc-400">No purchase orders yet.</p></div>
          ) : pos.map(po => (
            <div key={po.id} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid={`po-${po.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold text-white">{po.po_number}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded uppercase font-medium ${statusColors[po.status]}`}>{po.status}</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">Supplier: <span className="text-white">{po.supplier_name}</span></p>
                  <div className="flex gap-6 mt-2 text-sm">
                    <span className="text-zinc-400">Items: <span className="text-white">{po.items?.length || 0}</span></span>
                    <span className="text-zinc-400">Total: <span className="text-[#78BE20] font-bold">${po.total?.toFixed(2)}</span></span>
                    <span className="text-zinc-500">{fmt(po.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openViewModal(po)}><Eye className="h-4 w-4" /></Button>
                  {po.status === 'draft' && (
                    <>
                      <Button variant="outline" size="sm" className="text-zinc-400 hover:text-white" onClick={() => openEditModal(po)} data-testid={`edit-po-${po.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                      <Button variant="outline" size="sm" className="text-blue-400 hover:text-blue-300" onClick={() => handleSendPO(po.id)} data-testid={`send-po-${po.id}`}><Send className="h-4 w-4 mr-1" /> Send</Button>
                      <Button variant="outline" size="sm" className="text-green-400 hover:text-green-300" onClick={() => handleConfirmManual(po.id)} data-testid={`confirm-manual-po-${po.id}`} title="Mark as ordered without sending email"><PackageCheck className="h-4 w-4 mr-1" /> Mark Ordered</Button>
                      <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleCancelPO(po.id)}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                  {(po.status === 'sent' || po.status === 'partial') && (
                    <>
                      <Button variant="outline" size="sm" className="text-cyan-400 hover:text-cyan-300" onClick={() => handleResendPO(po.id)} title="Resend PO to supplier">
                        <RefreshCw className="h-4 w-4 mr-1" /> Resend
                      </Button>
                      <Button variant="outline" size="sm" className="text-green-400 hover:text-green-300" onClick={() => openReceiveModal(po)}>
                        <PackageCheck className="h-4 w-4 mr-1" /> Receive
                      </Button>
                      <Button variant="outline" size="sm" className="text-yellow-400 hover:text-yellow-300" onClick={() => handleClosePO(po.id)} title="Close PO - accept partial delivery">
                        <XCircle className="h-4 w-4 mr-1" /> Close
                      </Button>
                    </>
                  )}
                  {po.status !== 'cancelled' && po.status !== 'draft' && (
                    <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300" onClick={() => openDeleteDialog(po)} title="Force delete PO" data-testid={`delete-po-${po.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create PO Modal */}
        <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) resetForm(); }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white">{editingPO ? `Edit ${editingPO.po_number}` : 'Create Purchase Order'}</DialogTitle></DialogHeader>
            <form onSubmit={editingPO ? handleUpdatePO : handleCreatePO} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Supplier *</Label>
                  <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Expected Delivery Date</Label>
                  <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" />
                </div>
              </div>
              {form.supplier_id && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 hover:text-yellow-300"
                  onClick={handleAutoFillLowStock}
                  data-testid="auto-fill-low-stock"
                >
                  <Zap className="h-4 w-4 mr-2" /> Auto-fill Low Stock Items for This Supplier
                </Button>
              )}
              <div>
                <Label className="mb-2 block">Line Items</Label>
                <div className="space-y-2">
                  <div className="flex gap-2 items-end text-xs text-zinc-400 font-medium px-1">
                    <div className="flex-1">Product</div>
                    <div className="w-24">Qty</div>
                    <div className="w-32">Unit Cost ($)</div>
                    <div className="w-[36px]"></div>
                  </div>
                  {form.items.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Select value={item.product_id} onValueChange={(v) => updateLineItem(idx, 'product_id', v)}>
                          <SelectTrigger className="bg-zinc-800 border-zinc-700"><SelectValue placeholder="Select product" /></SelectTrigger>
                          <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} - {p.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="w-24">
                        {(() => {
                          const prod = products.find(p => p.id === item.product_id);
                          const minQty = prod?.supplier_moq || 1;
                          return (
                            <Input type="number" min={minQty} value={item.quantity} onChange={(e) => {
                              let val = parseInt(e.target.value) || 0;
                              if (val < minQty) val = minQty;
                              updateLineItem(idx, 'quantity', val);
                            }} className="bg-zinc-800 border-zinc-700" placeholder="Qty" title={minQty > 1 ? `Supplier MOQ: ${minQty}` : ''} />
                          );
                        })()}
                      </div>
                      <div className="w-32"><Input type="number" step="0.01" min="0" value={item.cost_price} onChange={(e) => updateLineItem(idx, 'cost_price', parseFloat(e.target.value) || 0)} className="bg-zinc-800 border-zinc-700" placeholder="Cost $" /></div>
                      <Button type="button" variant="outline" size="sm" onClick={() => removeLineItem(idx)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addLineItem}><Plus className="h-4 w-4 mr-1" /> Add Item</Button>
              </div>
              <div className="flex justify-between items-center p-3 bg-zinc-800 rounded">
                <span className="text-zinc-400">Subtotal:</span>
                <span className="text-lg font-bold text-white">${getTotal().toFixed(2)}</span>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" rows={2} /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#78BE20] text-black hover:bg-[#65a31b]">{editingPO ? 'Update PO' : 'Create PO'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View PO Modal */}
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white">{selectedPO?.po_number}</DialogTitle></DialogHeader>
            {selectedPO && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-zinc-500">Supplier:</span><p className="text-white">{selectedPO.supplier_name}</p></div>
                  <div><span className="text-zinc-500">Status:</span><p><Badge className={`${statusColors[selectedPO.status]}`}>{selectedPO.status}</Badge></p></div>
                  <div><span className="text-zinc-500">Created:</span><p className="text-white">{fmtTime(selectedPO.created_at)}</p></div>
                  {selectedPO.received_at && <div><span className="text-zinc-500">Received:</span><p className="text-white">{fmtTime(selectedPO.received_at)}</p></div>}
                  {selectedPO.expected_date && <div><span className="text-zinc-500">Expected:</span><p className="text-white">{selectedPO.expected_date}</p></div>}
                </div>

                <div className="border-t border-zinc-800 pt-4">
                  <h4 className="text-sm font-medium text-zinc-400 mb-2">Line Items</h4>
                  <div className="space-y-2">
                    {selectedPO.items?.map((item, idx) => {
                      const itemSerials = viewSerials.filter(s => s.product_id === item.product_id);
                      const isExpanded = expandedItem === idx;
                      return (
                        <div key={idx} className="bg-zinc-800 rounded overflow-hidden">
                          <div className="flex justify-between items-center p-3 text-sm">
                            <div>
                              <p className="text-white font-medium">{item.product_name}</p>
                              <p className="text-zinc-500 text-xs">{item.sku}</p>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-white">{item.quantity_received || 0} / {item.quantity} received</p>
                                <p className="text-zinc-400">${item.cost_price} ea</p>
                              </div>
                              {itemSerials.length > 0 && (
                                <Button variant="ghost" size="sm" onClick={() => setExpandedItem(isExpanded ? null : idx)} className="text-zinc-400 hover:text-white h-7 px-2" data-testid={`toggle-serials-${idx}`}>
                                  <Hash className="h-3 w-3 mr-1" />
                                  {itemSerials.length}
                                  {isExpanded ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                                </Button>
                              )}
                            </div>
                          </div>
                          {isExpanded && itemSerials.length > 0 && (
                            <div className="border-t border-zinc-700 px-3 py-2 bg-zinc-800/50">
                              <div className="flex justify-between items-center mb-1.5">
                                <p className="text-[10px] uppercase text-zinc-500">Serial Numbers</p>
                                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-[#78BE20] hover:text-[#65a31b]" onClick={() => handlePrintLabels(itemSerials.map(s => ({ serial_number: s.serial_number, product_name: item.product_name, sku: item.sku })))} data-testid={`print-labels-${idx}`}>
                                  <Printer className="h-3 w-3 mr-1" /> Print Labels
                                </Button>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                                {itemSerials.map((s, si) => (
                                  <div key={si} className="flex items-center gap-2 text-xs bg-zinc-900 rounded px-2 py-1.5">
                                    <Hash className="h-3 w-3 text-zinc-600 flex-shrink-0" />
                                    <span className="text-white font-mono">{s.serial_number}</span>
                                    <span className="text-zinc-600 ml-auto">{fmt(s.received_at)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-zinc-800 pt-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="text-zinc-400">Total (inc. GST):</span>
                    <span className="text-xl font-bold text-[#78BE20]">${selectedPO.total?.toFixed(2)}</span>
                  </div>
                  {viewSerials.length > 0 && (
                    <Button variant="outline" size="sm" className="border-zinc-700" onClick={() => handlePrintLabelsForPO(selectedPO.id)} data-testid="print-all-labels-btn">
                      <Printer className="h-3.5 w-3.5 mr-1.5" /> Print All Labels ({viewSerials.length})
                    </Button>
                  )}
                </div>
                {selectedPO.notes && (
                  <div className="border-t border-zinc-800 pt-4"><span className="text-zinc-500 text-sm">Notes:</span><p className="text-zinc-300 text-sm">{selectedPO.notes}</p></div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Receive Stock Modal */}
        <Dialog open={showReceiveModal} onOpenChange={setShowReceiveModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white">Receive Stock - {selectedPO?.po_number}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm">Enter quantities received. Serial numbers will be auto-generated if left blank (format: SKU-0001).</p>
              <div className="space-y-4">
                {receiveItems.map((item, idx) => {
                  const remaining = item.quantity_ordered - item.quantity_received;
                  const snList = serialNumbers[item.product_id] || [];
                  return (
                    <div key={idx} className="bg-zinc-800 rounded-lg overflow-hidden" data-testid={`receive-item-${idx}`}>
                      <div className="flex items-center gap-4 p-3">
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">{item.product_name}</p>
                          <p className="text-zinc-500 text-xs">{item.sku} · {remaining} remaining to receive</p>
                        </div>
                        <div className="text-center text-sm">
                          <p className="text-zinc-500 text-xs">Ordered</p>
                          <p className="text-white font-medium">{item.quantity_ordered}</p>
                        </div>
                        <div className="text-center text-sm">
                          <p className="text-zinc-500 text-xs">Received</p>
                          <p className="text-white font-medium">{item.quantity_received}</p>
                        </div>
                        <div className="w-24">
                          <Label className="text-xs text-zinc-500">Qty Now</Label>
                          <Input
                            type="number" min="0" max={remaining}
                            value={item.quantity_to_receive}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 0;
                              const n = [...receiveItems]; n[idx].quantity_to_receive = val; setReceiveItems(n);
                              // Auto-resize serial lines
                              const current = serialNumbers[item.product_id] || [];
                              if (val > current.length) {
                                setSerialNumbers({ ...serialNumbers, [item.product_id]: [...current, ...Array(val - current.length).fill('')] });
                              }
                            }}
                            className="bg-zinc-700 border-zinc-600"
                            data-testid={`receive-qty-${idx}`}
                          />
                        </div>
                      </div>
                      {item.quantity_to_receive > 0 && (
                        <div className="border-t border-zinc-700 px-3 py-3 bg-zinc-800/50">
                          {item.track_serials ? (
                            /* Serialised products — scanner-friendly individual input */
                            <div>
                              <Label className="text-xs text-zinc-400 flex items-center gap-1 mb-2">
                                <Hash className="h-3 w-3" /> Scan Serial Numbers
                                <span className="text-red-400 ml-1">* Required</span>
                              </Label>
                              <div className="flex gap-2 mb-2">
                                <Input
                                  autoFocus
                                  placeholder="Scan or type serial number..."
                                  className="bg-zinc-900 border-zinc-700 font-mono text-sm flex-1"
                                  data-testid={`scanner-input-${idx}`}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                      e.preventDefault();
                                      const val = e.target.value.trim();
                                      if (!val) return;
                                      const current = serialNumbers[item.product_id] || [];
                                      if (current.includes(val)) {
                                        toast.error(`Duplicate serial: ${val}`);
                                        e.target.select();
                                        return;
                                      }
                                      if (current.filter(s => s.trim()).length >= item.quantity_to_receive) {
                                        toast.error(`All ${item.quantity_to_receive} serials scanned`);
                                        return;
                                      }
                                      setSerialNumbers({ ...serialNumbers, [item.product_id]: [...current.filter(s => s.trim()), val] });
                                      e.target.value = '';
                                      e.target.focus();
                                    }
                                  }}
                                />
                                <Badge variant="outline" className={`shrink-0 px-3 ${(serialNumbers[item.product_id] || []).filter(s => s.trim()).length === item.quantity_to_receive ? 'border-green-500 text-green-400' : 'border-zinc-600 text-zinc-400'}`}>
                                  {(serialNumbers[item.product_id] || []).filter(s => s.trim()).length} / {item.quantity_to_receive}
                                </Badge>
                              </div>
                              {(serialNumbers[item.product_id] || []).filter(s => s.trim()).length > 0 && (
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                  {(serialNumbers[item.product_id] || []).filter(s => s.trim()).map((sn, si) => (
                                    <div key={si} className="flex items-center gap-2 text-xs bg-zinc-900 rounded px-2 py-1.5 group">
                                      <Hash className="h-3 w-3 text-green-500 flex-shrink-0" />
                                      <span className="font-mono text-white">{sn}</span>
                                      <button
                                        type="button"
                                        className="ml-auto text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={() => {
                                          const current = (serialNumbers[item.product_id] || []).filter(s => s.trim());
                                          setSerialNumbers({ ...serialNumbers, [item.product_id]: current.filter((_, i) => i !== si) });
                                        }}
                                      >
                                        <XCircle className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <p className="text-zinc-600 text-xs mt-1.5">Scan barcode or type serial + Enter. Duplicates are blocked.</p>
                            </div>
                          ) : (
                            /* Non-serialised — auto-generated, optional manual override */
                            <div>
                              <Label className="text-xs text-zinc-400 flex items-center gap-1 mb-2">
                                <Tag className="h-3 w-3" /> Serial Numbers (auto-generated if left blank)
                              </Label>
                              <Textarea
                                value={snList.join('\n')}
                                onChange={(e) => updateSerialInput(item.product_id, e.target.value)}
                                className="bg-zinc-900 border-zinc-700 font-mono text-sm"
                                rows={Math.min(item.quantity_to_receive, 3)}
                                placeholder={`Leave blank to auto-generate\ne.g. ${item.sku?.replace(/-/g,'')}-0001`}
                                data-testid={`serial-input-${idx}`}
                              />
                              <p className="text-zinc-600 text-xs mt-1">{snList.filter(s => s.trim()).length > 0 ? `${snList.filter(s => s.trim()).length} manual` : 'Will auto-generate'} — {item.quantity_to_receive} labels</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div><Label>Receiving Notes</Label><Textarea value={receiveNotes} onChange={(e) => setReceiveNotes(e.target.value)} className="mt-1 bg-zinc-800 border-zinc-700" rows={2} placeholder="Optional notes..." /></div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowReceiveModal(false)}>Cancel</Button>
                <Button onClick={handleReceivePO} className="bg-[#78BE20] text-black hover:bg-[#65a31b]" data-testid="confirm-receive-btn">
                  <PackageCheck className="h-4 w-4 mr-2" /> Receive Stock
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Force Delete PO Dialog */}

        {/* Print Labels After Receiving Dialog */}
        <Dialog open={showLabelDialog} onOpenChange={setShowLabelDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Tag className="h-5 w-5 text-[#78BE20]" />
                Serial Labels Ready
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm">
                {lastReceivedSerials.length} serial number{lastReceivedSerials.length !== 1 ? 's were' : ' was'} auto-generated. Would you like to print labels?
              </p>
              <div className="bg-zinc-800 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
                {lastReceivedSerials.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <Hash className="h-3 w-3 text-zinc-600" />
                    <span className="font-mono text-white">{s.serial_number}</span>
                    <span className="text-zinc-500 ml-auto">{s.sku}</span>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowLabelDialog(false)}>Skip</Button>
                <Button className="bg-[#78BE20] text-black hover:bg-[#65a31b]" onClick={() => { handlePrintLabels(lastReceivedSerials); setShowLabelDialog(false); }} data-testid="print-received-labels-btn">
                  <Printer className="h-4 w-4 mr-2" /> Print Labels
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Force Delete PO Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Delete {deletePO?.po_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {deleteSummary?.has_received_stock ? (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 text-sm font-medium mb-2">This PO has received stock!</p>
                  <div className="space-y-1 text-sm text-zinc-400">
                    <p>Ordered: <span className="text-white">{deleteSummary.total_ordered} items</span></p>
                    <p>Received: <span className="text-white">{deleteSummary.total_received} items</span></p>
                    {deleteSummary.serial_numbers > 0 && (
                      <p>Serial numbers: <span className="text-white">{deleteSummary.serial_numbers}</span>
                        {deleteSummary.sold_serials > 0 && <span className="text-red-400 ml-1">({deleteSummary.sold_serials} sold)</span>}
                      </p>
                    )}
                  </div>
                  {deleteSummary.has_sold_serials && (
                    <p className="text-red-400 text-xs mt-3 font-medium">
                      Warning: Some serial numbers from this PO have been sold to customers. Reversing stock will NOT affect sold serials.
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={reverseStock}
                      onChange={(e) => setReverseStock(e.target.checked)}
                      id="reverse-stock"
                      className="w-4 h-4 rounded accent-red-500"
                      data-testid="reverse-stock-toggle"
                    />
                    <label htmlFor="reverse-stock" className="text-sm text-zinc-300 cursor-pointer">
                      Reverse {deleteSummary.total_received} received items from stock levels
                    </label>
                  </div>
                </div>
              ) : (
                <p className="text-zinc-400 text-sm">This PO has no received stock. It will be permanently deleted.</p>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1 border-zinc-700" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                <Button variant="destructive" className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleForceDelete} data-testid="confirm-force-delete">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete Permanently
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
